import { useState, useEffect } from 'react';
import { getStickyPortalTargetState } from '../utils';

interface IUseStickyPortalOptions {
  enabled: boolean;
  stickyPortalContainer?: HTMLElement | string | (() => HTMLElement | null) | null;
}

export const useStickyPortal = (options: IUseStickyPortalOptions): HTMLElement | null => {
  const { enabled, stickyPortalContainer } = options;

  const [stickyPortalTarget, setStickyPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      setStickyPortalTarget(null);
      return;
    }

    let observer: MutationObserver | null = null;
    let latestShouldObserve = false;
    const syncStickyPortalTarget = (): void => {
      const { target, shouldObserve } = getStickyPortalTargetState(stickyPortalContainer);
      latestShouldObserve = shouldObserve;

      setStickyPortalTarget((prevTarget) => {
        return prevTarget === target ? prevTarget : target;
      });

      if (!shouldObserve) {
        observer?.disconnect();
        observer = null;
      }
    };

    syncStickyPortalTarget();

    if (latestShouldObserve && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        syncStickyPortalTarget();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer?.disconnect();
    };
  }, [enabled, stickyPortalContainer]);

  return stickyPortalTarget;
};
